/* All 11 reference vault modals (ported verbatim from vault-app modals/index.jsx) → TSX.
 * Preview + Share are wired to the real backend (data + handlers) while keeping the
 * reference DOM/classes 1:1; the rest are unchanged. */
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { useApp } from "./ctx";
import { Modal, Thumb, Kv, ToggleRow, Switch } from "./Common";
import Ic from "./Ic";
import { useToast, promptDialog, confirmDialog } from "@/shared/ui";
import { useAuth } from "@/features/auth/context/AuthContext";
import { apiPost } from "@/features/shared/apiClient";
import { downloadBlob } from "@/util/download";
import {
  getModel, listModels, listEmbeds, createEmbed, updateEmbed,
  updateModel, starModel, deleteModel, createFolder, listChildren,
  type VaultModel, type VaultEmbed, type VaultFolder,
} from "@/features/vault/vaultApi";
import { shareModelWith, type ShareAccess } from "@/features/vault/sharedApi";
import { listCollections, createCollection, addItems, type Collection } from "@/features/collections/collectionsApi";
import { listVersions, restoreVersion, saveModelMeta, type ModelVersion } from "@/features/vault/versionsApi";
import EmbedSettings from "@/features/embed/EmbedSettings";
import { getGoldRate, saveQuote, getQuote, type GoldRate } from "@/features/rates/ratesApi";
import { getActivity, type ActivityItem } from "@/features/insights/insightsApi";
import { getEffectiveSettings, getScopeSettings, saveScopeSettings, type RenderValues } from "@/features/rendersettings/renderSettingsApi";
import { createBatch, listBatches, listAgents, type RenderBatch, type Agent } from "@/features/render/renderApi";
import { useUploads } from "@/features/vault/useUploads";
import { uploadAsset, type AssetKind } from "@/features/assets/assetsApi";


const inr = (n: number) => "₹" + Math.round(n).toLocaleString("en-IN");
const numOr = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const fmtSize = (bytes?: number) => {
  if (!bytes) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let v = bytes, i = 0;
  while (v >= 1024 && i < u.length - 1) (v /= 1024), i++;
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const hasRenderOverride = (m: VaultModel) => !!m.config && Object.keys(m.config).length > 0;
const relTime = (iso: string): string => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = s / 60; if (m < 60) return `${Math.floor(m)} min ago`;
  const h = m / 60; if (h < 24) return `${Math.floor(h)} h ago`;
  return `${Math.floor(h / 24)} d ago`;
};
const ACT_VERB: Record<ActivityItem["type"], string> = { link_view: "viewed", download: "downloaded", approve: "approved", comment: "commented on" };
const ACT_ICON: Record<ActivityItem["type"], string> = { link_view: "eye", download: "image", approve: "check", comment: "pin" };
const ACT_BG: Record<ActivityItem["type"], string> = { link_view: "var(--green-soft)", download: "var(--accent-soft)", approve: "var(--green-soft)", comment: "var(--red-soft)" };
const METAL_OPTS: [string, string][] = [["yellow-gold", "18k Yellow"], ["rose-gold", "18k Rose"], ["white-gold", "14k White"], ["platinum", "Platinum"]];
const ANGLE_OPTS: [string, string][] = [["perspective", "Perspective ¾"], ["macro", "Macro (stone)"], ["top", "Top"]];
const RES_LABEL: Record<string, string> = { "1920x1080": "1080p", "2560x1440": "2K", "3840x2160": "4K", "7680x4320": "8K" };

/* ───────────── file preview (tabs + live quote) ───────────── */
function PreviewModal() {
  return (
    <Modal id="preview" maxWidth={1060} plain>
      <PreviewBody />
    </Modal>
  );
}

function PreviewBody() {
  const { close, open, go, modalData, setModalData } = useApp();
  const seed = (modalData.preview as VaultModel | null) ?? null;
  const [model, setModel] = useState<VaultModel | null>(seed);
  const [tab, setTab] = useState("details");

  useEffect(() => {
    if (!seed) return;
    let live = true;
    getModel(seed._id).then((m) => live && setModel(m)).catch(() => {});
    return () => { live = false; };
  }, [seed?._id]);

  // Opened without a file (e.g. a "View" button that didn't thread a model): show
  // an honest placeholder rather than fabricated demo data.
  if (!model) {
    return (
      <>
        <div className="m-head" style={{ margin: "-18px -22px 0", position: "relative" }}>
          <h2>Preview</h2>
          <button className="m-close" onClick={() => close("preview")}>✕</button>
        </div>
        <div style={{ padding: "44px 8px", textAlign: "center" }}>
          <div className="hint">No file selected. Open a model from your drive to preview it here.</div>
        </div>
      </>
    );
  }

  const name = model.name;
  const fmt = model.format.toUpperCase();
  const showOverride = hasRenderOverride(model);
  const openViewer = () => { close("preview"); go(`/view/${model._id}`); };
  const onCanvas = () => { close("preview"); go(`/studio/${model._id}`); };
  const onShare = () => { setModalData("share", model); close("preview"); open("share"); };

  return (
    <>
      <div className="m-head" style={{ margin: "-18px -22px 0", position: "relative" }}>
        <span className="mini-ico" style={{ background: "#7A5C2E" }}>{fmt}</span>
        <h2>{name}</h2>
        {showOverride && <span className="badge gold">⚙ Model render override</span>}
        <button className="btn ghost" style={{ padding: "7px 14px" }} onClick={onCanvas}><Ic n="sparkle" size={14} /> Open in Canvas</button>
        <button className="btn gold" style={{ padding: "7px 14px" }} onClick={onShare}>Share</button>
        <button className="m-close" onClick={() => close("preview")}>✕</button>
      </div>
      <div className="pv" style={{ margin: "0 -22px -22px" }}>
        <div className="pv-stage" style={{ cursor: "pointer" }} onClick={openViewer} title="Open in the 3D viewer">
          {model.posterUrl
            ? <img src={model.posterUrl} alt={name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
            : <Thumb art="diaSol" bg="bgWarm" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />}
          <div className="pv-tools" onClick={(e) => e.stopPropagation()}>
            <button title="Open the 3D viewer" onClick={openViewer}>⛶</button>
          </div>
          <span className="badge gray" style={{ position: "absolute", top: 14, left: 14, background: "rgba(255,255,255,.9)" }}>Poster preview · click to open the 3D viewer</span>
        </div>
        <div className="pv-side">
          <div className="tabs">
            {[["details", "Details"], ["render", "Render settings"], ["quote", "Quote"], ["versions", "Versions"], ["activity", "Activity"]].map(([k, l]) => (
              <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
          {tab === "details" && <DetailsPane model={model} />}
          {tab === "render" && <RenderPane model={model} />}
          {tab === "quote" && <QuotePane model={model} />}
          {tab === "versions" && <VersionsPane model={model} onCompare={() => { setModalData("compare", model); close("preview"); open("compare"); }} />}
          {tab === "activity" && <ActivityPane model={model} />}
        </div>
      </div>
    </>
  );
}

// ── Details ──────────────────────────────────────────────────────────────────
function DetailsPane({ model }: { model: VaultModel }) {
  const metals = model.layers.filter((l) => l.type === "metal").map((l) => l.name);
  const gems = model.layers.filter((l) => l.type === "gem").map((l) => l.name);
  return (
    <div className="tab-pane on">
      <Kv k="SKU" v={model.name} />
      <Kv k="Type" v={`${model.format.toUpperCase()} model`} />
      <Kv k="Size" v={fmtSize(model.size) + (model.glbSize ? ` · ${fmtSize(model.glbSize)} optimized` : "")} />
      <Kv k="Metal" v={metals.length ? metals.join(", ") : "—"} />
      <Kv k="Center stone" v={gems.length ? gems.join(", ") : "—"} />
      <CertRow model={model} />
      <Kv k="Location" v={`My Drive${model.category ? ` › ${model.category}` : ""}`} />
      {model.tags.length > 0 && <div className="anno" style={{ marginTop: 12 }}>Tags: {model.tags.join(", ")}</div>}
      <div className="anno" style={{ marginTop: 12 }}>ⓘ Auto-extracted from the source file on upload — format, size and material layers need no manual entry.</div>
    </div>
  );
}

type CertFields = { certNumber?: string; certLab?: string };
function CertRow({ model }: { model: VaultModel }) {
  const { toast } = useToast();
  const m = model as VaultModel & CertFields;
  const [lab, setLab] = useState(m.certLab || "GIA");
  const [num, setNum] = useState(m.certNumber || "");
  const [saved, setSaved] = useState({ lab: m.certLab || "", num: m.certNumber || "" });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const u = (await saveModelMeta(model._id, { certNumber: num.trim(), certLab: lab.trim() || "GIA" })) as VaultModel & CertFields;
      setSaved({ lab: u.certLab || "GIA", num: u.certNumber || "" });
      setLab(u.certLab || "GIA"); setNum(u.certNumber || "");
      setEditing(false);
      toast({ tone: "success", title: "Certificate saved" });
    } catch (e) {
      toast({ tone: "error", title: "Couldn't save certificate", description: e instanceof Error ? e.message : undefined });
    } finally { setSaving(false); }
  };
  return (
    <div className="kv">
      <b>Certificate</b>
      {editing ? (
        <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input value={lab} onChange={(e) => setLab(e.target.value)} placeholder="Lab" style={{ width: 54, height: 28, border: "1px solid var(--border)", borderRadius: 8, padding: "0 8px" }} />
          <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="Cert #" style={{ width: 118, height: 28, border: "1px solid var(--border)", borderRadius: 8, padding: "0 8px" }} />
          <button className="btn ghost" style={{ padding: "4px 8px", fontSize: 11 }} disabled={saving} onClick={save}>{saving ? "…" : "Save"}</button>
        </span>
      ) : (
        <span style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }} onClick={() => setEditing(true)} title="Edit certificate">
          <span className="badge blue">{saved.num ? `${saved.lab || "GIA"} · ${saved.num}` : "GIA · not linked"}</span>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>Edit</span>
        </span>
      )}
    </div>
  );
}

// ── Render settings ──────────────────────────────────────────────────────────
function RenderPane({ model }: { model: VaultModel }) {
  const { toast } = useToast();
  const { go, close } = useApp();
  const [effective, setEffective] = useState<RenderValues | null>(null);
  const [fileScope, setFileScope] = useState<RenderValues>({});
  const [err, setErr] = useState(false);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([getEffectiveSettings(model._id), getScopeSettings("file", model._id)])
      .then(([eff, raw]) => { if (live) { setEffective(eff); setFileScope(raw); } })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [model._id]);

  const overrideCount = Object.keys(fileScope).length;
  const persist = async (nextScope: RenderValues, patch: RenderValues) => {
    const prevScope = fileScope;
    const prevEffective = effective;
    setFileScope(nextScope);
    setEffective((e) => ({ ...(e || {}), ...patch }));
    try { await saveScopeSettings("file", model._id, nextScope); }
    catch (e) {
      // Roll back the optimistic override so the "Overridden here" badge doesn't lie
      // about a save that never landed.
      setFileScope(prevScope);
      setEffective(prevEffective);
      toast({ tone: "error", title: "Couldn't save override", description: e instanceof Error ? e.message : undefined });
    }
  };
  const pickMetal = (v: string) => persist({ ...fileScope, metal: v }, { metal: v });
  const toggleAngle = (v: string) => {
    const cur = Array.isArray(effective?.angles) ? (effective!.angles as string[]) : [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    return persist({ ...fileScope, angles: next }, { angles: next });
  };
  const reset = async () => {
    setFileScope({});
    try { await saveScopeSettings("file", model._id, {}); setEffective(await getEffectiveSettings(model._id)); }
    catch (e) { toast({ tone: "error", title: "Couldn't reset", description: e instanceof Error ? e.message : undefined }); }
  };
  const reRender = async () => {
    setRendering(true);
    try {
      await createBatch({
        fileIds: [model._id], compute: "cloud",
        resolution: String(effective?.resolution ?? "3840x2160"), dpi: 300,
        options: { metal: effective?.metal ?? "yellow-gold", angles: effective?.angles ?? [] },
      });
      toast({ tone: "success", title: "Re-render queued" });
      close("preview"); go("/bulk");
    } catch (e) {
      toast({ tone: "error", title: "Couldn't queue render", description: e instanceof Error ? e.message : undefined });
      setRendering(false);
    }
  };

  if (err) return <div className="tab-pane on"><div className="hint">Render settings unavailable — set them on the Render Settings page.</div></div>;
  if (effective === null) return <div className="tab-pane on"><div className="hint">Loading render settings…</div></div>;

  const metal = String(effective.metal ?? "");
  const angles = Array.isArray(effective.angles) ? (effective.angles as string[]) : [];
  const res = String(effective.resolution ?? "");
  return (
    <div className="tab-pane on">
      <div className="inherit-note" style={{ marginBottom: 12, fontSize: 12 }}>
        {overrideCount > 0 ? (
          <><b>Overriding:</b>&nbsp;{overrideCount} setting{overrideCount === 1 ? "" : "s"} differ from Global.
            <button className="btn ghost" style={{ marginLeft: "auto", padding: "5px 10px", fontSize: 11.5 }} onClick={reset}>Reset</button></>
        ) : <span>All settings inherited from Global.</span>}
      </div>
      <div className="setting" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="s-top"><b>Metal for renders</b><span className={"inh-badge" + ("metal" in fileScope ? " ovr" : "")}>{"metal" in fileScope ? "Overridden here" : "Inherited · Global"}</span></div>
        <div className="pill-opts">{METAL_OPTS.map(([val, lbl]) => <button key={val} className={metal === val ? "on" : ""} onClick={() => void pickMetal(val)}>{lbl}</button>)}</div>
      </div>
      <div className="setting" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="s-top"><b>Camera angles</b><span className={"inh-badge" + ("angles" in fileScope ? " ovr" : "")}>{"angles" in fileScope ? "Overridden here" : "Inherited · Global"}</span></div>
        <div className="pill-opts">{ANGLE_OPTS.map(([val, lbl]) => <button key={val} className={angles.includes(val) ? "on" : ""} onClick={() => void toggleAngle(val)}>{lbl}</button>)}</div>
      </div>
      <div className="setting"><div className="s-top"><b>Resolution</b><span className="inh-badge">Inherited · Global{res ? ` (${RES_LABEL[res] ?? res})` : ""}</span></div></div>
      <button className="btn primary" style={{ width: "100%", justifyContent: "center", marginTop: 14 }} disabled={rendering} onClick={reRender}><Ic n="bolt" size={14} /> {rendering ? "Queuing…" : "Re-render with these settings"}</button>
    </div>
  );
}

// ── Quote ────────────────────────────────────────────────────────────────────
// Module-level so it keeps a stable component identity across QuotePane re-renders.
// Defined inline in render it would be a new type every keystroke → React remounts
// the <input> and the field loses focus after each character.
function QuoteField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="field" style={{ marginTop: 6 }}><label>{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} /></div>
  );
}

function QuotePane({ model }: { model: VaultModel | null }) {
  const { toast } = useToast();
  const [q, setQ] = useState({ w: 3.42, r: 7250, wa: 8, m: 900, s: 185000 });
  const [rate, setRate] = useState<GoldRate | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      model ? getQuote(model._id).catch(() => null) : Promise.resolve(null),
      getGoldRate().catch(() => null),
    ]).then(([savedQuote, live]) => {
      if (!alive) return;
      if (live) setRate(live);
      if (savedQuote?.params) {
        const p = savedQuote.params;
        setQ((prev) => ({ w: numOr(p.weight, prev.w), r: numOr(p.rate, prev.r), wa: numOr(p.wastage, prev.wa), m: numOr(p.making, prev.m), s: numOr(p.stones, prev.s) }));
      } else if (live) {
        setQ((prev) => ({ ...prev, r: live.pricePerGram }));
      }
    });
    return () => { alive = false; };
  }, [model?._id]);

  const metal = q.w * q.r * (1 + q.wa / 100), making = q.w * q.m;
  const sub = metal + making + q.s, gst = sub * .03, total = sub + gst;
  const quoteParams = { weight: q.w, rate: q.r, wastage: q.wa, making: q.m, stones: q.s };
  const summary = `Estimate: ${inr(total)} (metal ${inr(metal)}, making ${inr(making)}, stones ${inr(q.s)}, +3% GST)`;

  const downloadPdf = async () => {
    if (!model) return;
    setPdfBusy(true);
    try {
      await saveQuote({ modelId: model._id, params: quoteParams, total });
      const token = localStorage.getItem("auth_token");
      const ws = localStorage.getItem("active_workspace_id");
      const res = await fetch(`/api/v1/quotes/${model._id}/pdf`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(ws ? { "X-Workspace-Id": ws } : {}) },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.message || "Couldn't generate PDF");
      downloadBlob(await res.blob(), `${model.name}-quote.pdf`);
      toast({ tone: "success", title: "Quote PDF downloaded" });
    } catch (e) {
      toast({ tone: "error", title: "Couldn't generate PDF", description: e instanceof Error ? e.message : undefined });
    } finally { setPdfBusy(false); }
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(summary)}`, "_blank");

  return (
    <div className="tab-pane on">
      <div className="hint" style={{ marginBottom: 8 }}>Live quote from the CAD's metal weight + today's rates. Edit anything — it recalculates.</div>
      <div className="frow"><QuoteField label="Metal weight (g)" value={q.w} onChange={(v) => setQ((s) => ({ ...s, w: v }))} /><QuoteField label={`Gold rate ₹/g${rate ? " (LIVE)" : ""}`} value={q.r} onChange={(v) => setQ((s) => ({ ...s, r: v }))} /></div>
      <div className="frow"><QuoteField label="Wastage %" value={q.wa} onChange={(v) => setQ((s) => ({ ...s, wa: v }))} /><QuoteField label="Making ₹/g" value={q.m} onChange={(v) => setQ((s) => ({ ...s, m: v }))} /></div>
      <QuoteField label="Stones (₹)" value={q.s} onChange={(v) => setQ((s) => ({ ...s, s: v }))} />
      <div style={{ marginTop: 8 }}>
        <Kv k="Metal + wastage" v={inr(metal)} /><Kv k="Making charges" v={inr(making)} /><Kv k="GST (3%)" v={inr(gst)} />
      </div>
      <div className="q-total"><span>Client quote</span><b>{inr(total)}</b></div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button className="btn ghost" style={{ flex: 1, justifyContent: "center" }} disabled={!model || pdfBusy} onClick={downloadPdf}>{pdfBusy ? "Preparing…" : "Quote PDF"}</button>
        <button className="btn wa" style={{ flex: 1, justifyContent: "center", border: "none" }} onClick={whatsapp}>Send on WhatsApp</button>
      </div>
      {rate && <div className="hint" style={{ marginTop: 6 }}>24k gold {inr(rate.pricePerGram)}/g · {rate.source} · {new Date(rate.ts).toLocaleTimeString()}</div>}
    </div>
  );
}

// ── Versions ─────────────────────────────────────────────────────────────────
function VersionsPane({ model, onCompare }: { model: VaultModel; onCompare: () => void }) {
  const { toast } = useToast();
  const [versions, setVersions] = useState<ModelVersion[] | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listVersions(model._id).then((v) => alive && setVersions(v)).catch(() => alive && setVersions([]));
    return () => { alive = false; };
  }, [model._id]);

  const restore = async (v: ModelVersion) => {
    setRestoring(v._id);
    try { await restoreVersion(model._id, v._id); toast({ tone: "success", title: `Restored version ${v.version}`, description: "Reopen this model in Canvas to load it." }); }
    catch (e) { toast({ tone: "error", title: "Restore failed", description: e instanceof Error ? e.message : undefined }); }
    finally { setRestoring(null); }
  };

  return (
    <div className="tab-pane on">
      {versions === null ? <div className="hint">Loading versions…</div>
        : versions.length === 0 ? <div className="hint">No saved versions yet. Saving this model's scene in Canvas drops a restorable snapshot here.</div>
          : versions.map((v, i) => (
            <Kv key={v._id} k={`Version ${v.version}${i === 0 ? " · current" : ""}`} v={
              <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                {new Date(v.createdAt).toLocaleString()} · {v.note || "scene snapshot"}
                {i > 0 && <button className="btn ghost" style={{ padding: "3px 8px", fontSize: 11 }} disabled={restoring === v._id} onClick={() => void restore(v)}>{restoring === v._id ? "…" : "restore"}</button>}
              </span>
            } />
          ))}
      <button className="btn primary" style={{ width: "100%", justifyContent: "center", marginTop: 12 }} onClick={onCompare}><Ic n="compare" size={14} /> Compare versions</button>
    </div>
  );
}

// ── Activity ─────────────────────────────────────────────────────────────────
function ActivityPane({ model }: { model: VaultModel }) {
  const [items, setItems] = useState<ActivityItem[] | null>(null);
  useEffect(() => {
    let alive = true;
    getActivity(model._id).then((r) => alive && setItems(r)).catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [model._id]);
  return (
    <div className="tab-pane on">
      {items === null ? <div className="hint">Loading activity…</div>
        : items.length === 0 ? <div className="hint">No activity yet. Views, downloads, comments and approvals for this file appear here once its share links are opened.</div>
          : items.map((it, i) => (
            <Kv key={i} k={<>{it.clientName ?? "Someone"} {ACT_VERB[it.type]} {it.fileName ?? "this file"}</>} v={relTime(it.ts)} />
          ))}
    </div>
  );
}

/* ───────────── share ───────────── */
function ShareModal() {
  const { modalData } = useApp();
  const model = ((modalData.share ?? modalData.preview) as VaultModel | null) ?? null;
  return (
    <Modal id="share" title={model ? `Share “${model.name}”` : "Share"}>
      <ShareBody model={model} />
    </Modal>
  );
}

function ShareBody({ model }: { model: VaultModel | null }) {
  const { close } = useApp();
  if (!model) {
    return (
      <div>
        <div className="hint" style={{ marginBottom: 14 }}>Open a file and hit Share to create its link — sharing is per model.</div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn primary" onClick={() => close("share")}>Done</button>
        </div>
      </div>
    );
  }
  return <ShareBodyWired model={model} />;
}

function ShareBodyWired({ model }: { model: VaultModel }) {
  const { close, setClient } = useApp();
  const { toast } = useToast();
  const [embed, setEmbed] = useState<VaultEmbed | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [accessRole, setAccessRole] = useState("view");
  const [expiryDate, setExpiryDate] = useState("");
  const [allowDownload, setAllowDownload] = useState(false);
  const [visibility, setVisibility] = useState<VaultModel["visibility"]>(model.visibility);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("view");
  const [copied, setCopied] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const base = window.location.origin;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const existing = await listEmbeds(model._id);
        const e = existing[0] ?? (await createEmbed(model._id, [])).embed;
        if (!alive) return;
        setEmbed(e);
        setAccessRole(e.access);
        setExpiryDate(e.expiresAt ? e.expiresAt.slice(0, 10) : "");
        setAllowDownload(!!e.options?.allowDownload);
      } catch (err) {
        if (alive) setLoadErr(err instanceof Error ? err.message : "Couldn't create a share link.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [model._id]);

  const shareLink = embed ? `${base}/embed/${embed.token}` : "";

  const persistShare = async (share: { access?: string; expiresAt?: string | null }) => {
    if (!embed) return;
    try { const r = await updateEmbed(embed._id, embed.allowedDomains, embed.options, share); setEmbed(r.embed); }
    catch (e) {
      // Revert the optimistic select/date back to the last-saved embed (matches the
      // rollback the download/public toggles already do — this pair was missing it).
      setAccessRole(embed.access);
      setExpiryDate(embed.expiresAt ? embed.expiresAt.slice(0, 10) : "");
      toast({ tone: "error", title: "Couldn't update link", description: e instanceof Error ? e.message : undefined });
    }
  };
  const changeAccess = (v: string) => { setAccessRole(v); void persistShare({ access: v }); };
  const changeExpiry = (v: string) => { setExpiryDate(v); void persistShare({ expiresAt: v || null }); };
  const toggleDownload = async (next: boolean) => {
    if (!embed) return;
    setAllowDownload(next);
    try { const r = await updateEmbed(embed._id, embed.allowedDomains, { ...(embed.options ?? {}), allowDownload: next }); setEmbed(r.embed); }
    catch (e) { setAllowDownload(!next); toast({ tone: "error", title: "Couldn't update", description: e instanceof Error ? e.message : undefined }); }
  };
  const togglePublic = async (next: boolean) => {
    const prev = visibility;
    const v: VaultModel["visibility"] = next ? "public" : "shared";
    setVisibility(v);
    try { const m = await updateModel(model._id, { visibility: v }); setVisibility(m.visibility); }
    catch (e) { setVisibility(prev); toast({ tone: "error", title: "Couldn't update visibility", description: e instanceof Error ? e.message : undefined }); }
  };
  const copyLink = () => { if (!shareLink) return; void navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1400); };
  const setDomains = async (next: string[]) => {
    if (!embed) return;
    try { const r = await updateEmbed(embed._id, next, embed.options); setEmbed(r.embed); }
    catch (e) { toast({ tone: "error", title: "Couldn't update domains", description: e instanceof Error ? e.message : undefined }); }
  };
  const addDomain = () => {
    const d = domainInput.trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!d || !embed || embed.allowedDomains.includes(d)) return;
    setDomainInput(""); void setDomains([...embed.allowedDomains, d]);
  };
  const snippet = embed ? `<iframe src="${shareLink}" width="640" height="480" frameborder="0" allow="xr-spatial-tracking; fullscreen" allowfullscreen></iframe>` : "";
  const copySnippet = () => { if (!snippet) return; void navigator.clipboard.writeText(snippet); setSnippetCopied(true); setTimeout(() => setSnippetCopied(false), 1400); };
  const whatsapp = () => { if (shareLink) window.open(`https://wa.me/?text=${encodeURIComponent(shareLink)}`, "_blank"); };
  const emailShare = async () => {
    if (!embed) return;
    const to = (await promptDialog({ title: "Email the share link to", label: "Email", defaultValue: inviteEmail || "" }))?.trim();
    if (!to) return;
    try { await apiPost("/share/email", { token: embed.token, to }); toast({ tone: "success", title: `Shared with ${to}` }); }
    catch (e) { toast({ tone: "error", title: "Couldn't send email", description: e instanceof Error ? e.message : undefined }); }
  };
  const qr = async () => {
    if (!shareLink) return;
    try {
      const url = await QRCode.toDataURL(shareLink, { margin: 1, width: 512 });
      const a = document.createElement("a"); a.href = url; a.download = `${model.name}-qr.png`; a.click();
      toast({ tone: "success", title: "QR downloaded" });
    } catch (e) { toast({ tone: "error", title: "Couldn't make QR", description: e instanceof Error ? e.message : undefined }); }
  };
  // The invite select has an "approve" option the share grant doesn't model — fold
  // it into "comment" (the closest reviewer access); anything unknown → "view".
  const roleToAccess = (r: string): ShareAccess =>
    r === "download" || r === "comment" ? r : r === "approve" ? "comment" : "view";
  const invite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    try {
      await shareModelWith(model._id, email, roleToAccess(inviteRole));
      toast({ tone: "success", title: `Shared with ${email}` });
      setInviteEmail("");
    } catch (e) {
      toast({ tone: "error", title: "Couldn't share", description: e instanceof Error ? e.message : undefined });
    }
  };

  if (loading) return <div className="hint" style={{ padding: "24px 0", textAlign: "center" }}>Preparing share…</div>;
  if (!embed) return (
    <div style={{ padding: "8px 0" }}>
      <div className="hint" style={{ marginBottom: 12 }}>
        Couldn't create a share link for this file{loadErr ? ` — ${loadErr.toLowerCase().includes("ready") ? "it's still processing; try again once it's ready" : loadErr}` : ""}.
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn primary" onClick={() => close("share")}>Close</button>
      </div>
    </div>
  );

  return (
    <>
      <div className="field" style={{ marginTop: 0 }}><label>Invite people</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="text" placeholder="name@client.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} style={{ flex: 1, height: 38, border: "1px solid var(--border)", borderRadius: 10, padding: "0 12px" }} />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={{ width: 150, height: 38, border: "1px solid var(--border)", borderRadius: 10, padding: "0 8px" }}><option value="view">Can view</option><option value="download">Can download</option><option value="comment">Can comment</option><option value="approve">Can approve</option></select>
          <button className="btn primary" onClick={invite}>Invite</button>
        </div>
      </div>
      <div className="sec-head" style={{ margin: "20px 2px 10px" }}>Or share by link</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="text" readOnly value={shareLink} style={{ flex: 1, height: 40, border: "1px solid var(--border)", borderRadius: 10, padding: "0 12px", background: "var(--surface-2)", fontSize: 12.5 }} />
        <button className={"btn gold" + (copied ? " copy-flash" : "")} onClick={copyLink}>{copied ? "✓ Copied" : "Copy link"}</button>
        <button className="btn wa" title="Send via WhatsApp" style={{ border: "none" }} onClick={whatsapp}>WhatsApp</button>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button className="btn ghost" style={{ flex: 1, justifyContent: "center", fontSize: 12 }} onClick={() => toast({ tone: "info", title: "PDF lookbook — coming soon" })}>PDF lookbook</button>
        <button className="btn ghost" style={{ flex: 1, justifyContent: "center", fontSize: 12 }} onClick={emailShare}><Ic n="mail" size={13} /> Email</button>
        <button className="btn ghost" style={{ flex: 1, justifyContent: "center", fontSize: 12 }} onClick={qr}><Ic n="qr" size={13} /> QR for showroom</button>
      </div>
      <div style={{ marginTop: 8 }}>
        <ToggleRow title="Access level" sub="What anyone with the link can do."
          right={<select value={accessRole} onChange={(e) => changeAccess(e.target.value)} style={{ width: 150, height: 34, border: "1px solid var(--border)", borderRadius: 9, padding: "0 8px" }}><option value="view">Can view</option><option value="download">Can download</option><option value="comment">Can comment</option><option value="approve">Can approve</option></select>} />
        <ToggleRow title="Expiry" sub="Link stops working after this date."
          right={<input type="date" value={expiryDate} onChange={(e) => changeExpiry(e.target.value)} style={{ width: 150, height: 34, border: "1px solid var(--border)", borderRadius: 9, padding: "0 10px" }} />} />
        <ToggleRow title="Password protect" sub="Visitors must enter a password." right={<span className="badge gray">Soon</span>} />
        <ToggleRow title="Watermark previews" sub="Your logo over every render until they download." right={<span className="badge gray">Soon</span>} />
        <ToggleRow title="Allow 3D model download" sub="Off = clients see renders & turntables only, never the CAD."
          right={<Switch on={allowDownload} onChange={toggleDownload} />} />
        <ToggleRow title="Make public" sub="List in your public showcase — anyone with the showcase link can view."
          right={<Switch on={visibility === "public"} onChange={togglePublic} />} />
        <ToggleRow title="Notify me on open" sub="Email + in-app ping when the client views." right={<span className="badge gray">Soon</span>} />
      </div>

      {/* Domain allow-list (embed lockdown) */}
      <div className="sec-head" style={{ margin: "18px 2px 8px" }}>Allowed domains <span className="n">· {embed.allowedDomains.length ? "locked to these" : "any site"}</span></div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {embed.allowedDomains.map((d) => (
          <span key={d} className="chip" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{d}
            <button className="m-close" style={{ width: 18, height: 18, fontSize: 11 }} title="Remove" onClick={() => void setDomains(embed.allowedDomains.filter((x) => x !== d))}>✕</button>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="text" placeholder="example.com (blank = embeddable anywhere)" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addDomain(); }} style={{ flex: 1, height: 36, border: "1px solid var(--border)", borderRadius: 10, padding: "0 12px", fontSize: 12.5 }} />
        <button className="btn ghost" onClick={addDomain}>Add</button>
      </div>

      {/* Embed snippet + white-label editor */}
      <div className="sec-head" style={{ margin: "18px 2px 8px" }}>Embed on your site</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input type="text" readOnly value={snippet} style={{ flex: 1, height: 36, border: "1px solid var(--border)", borderRadius: 10, padding: "0 12px", background: "var(--surface-2)", fontSize: 11.5, fontFamily: "ui-monospace, Menlo, monospace" }} />
        <button className={"btn ghost" + (snippetCopied ? " copy-flash" : "")} onClick={copySnippet}>{snippetCopied ? "✓ Copied" : "Copy"}</button>
      </div>
      <button className="btn ghost" style={{ width: "100%", justifyContent: "center", marginTop: 8, fontSize: 12 }} onClick={() => setShowEmbed((v) => !v)}><Ic n="sliders" size={13} /> {showEmbed ? "Hide viewer & branding" : "Customize viewer & branding"}</button>
      {showEmbed && (
        <div style={{ marginTop: 10, border: "1px solid var(--border)", borderRadius: 12, padding: 12 }}>
          <EmbedSettings model={model} onClose={() => setShowEmbed(false)} />
        </div>
      )}

      <div className="hint" style={{ marginTop: 12 }}>{embed.viewCount} views</div>
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button className="btn ghost" style={{ flex: 1, justifyContent: "center" }} onClick={() => { close("share"); setClient(true); }}><Ic n="eye" size={14} /> Preview as client</button>
        <button className="btn primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => close("share")}>Done</button>
      </div>
    </>
  );
}

/* ───────────── upload / ctx ───────────── */
const readDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error ?? new Error("Read failed"));
    r.readAsDataURL(file);
  });

/* Real upload entry (Sidebar "New / Upload"). Uploads at the drive root. The modal
 * has no direct handle on the Drive page's loader, so it signals through the vault
 * context's drive revision (bumpDrive) — the open listing re-fetches immediately
 * instead of staying stale until the next navigation. */
function UploadModal() {
  const { close, bumpDrive } = useApp();
  const { toast } = useToast();
  const { uploads, uploadFiles } = useUploads(bumpDrive);
  const modelInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const assetInput = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const doUpload = (files: FileList | null) => {
    if (!files?.length) return;
    void uploadFiles(files, null);
    close("upload");
  };
  const pickAssets = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const kind: AssetKind = file.type.startsWith("image/") ? "image" : "doc";
        await uploadAsset({ name: file.name, kind, dataUrl: await readDataUrl(file) });
      }
      toast({ tone: "success", title: `Uploaded ${files.length} file${files.length === 1 ? "" : "s"}` });
      close("upload");
    } catch (e) {
      toast({ tone: "error", title: "Upload failed", description: e instanceof Error ? e.message : undefined });
    } finally { setBusy(false); }
  };
  const newFolder = async () => {
    const name = (await promptDialog({ title: "New folder", label: "Folder name" }))?.trim();
    if (!name) return;
    try { await createFolder(name, null); bumpDrive(); toast({ tone: "success", title: `Folder “${name}” created` }); close("upload"); }
    catch (e) { toast({ tone: "error", title: "Couldn't create folder", description: e instanceof Error ? e.message : undefined }); }
  };
  const openFolder = () => {
    const el = folderInput.current;
    if (!el) return;
    (el as unknown as { webkitdirectory: boolean }).webkitdirectory = true;
    el.click();
  };

  return (
    <Modal id="upload" maxWidth={480} title="New">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button className="btn ghost" style={{ justifyContent: 'flex-start', padding: 14 }} onClick={() => void newFolder()}>New folder</button>
        <button className="btn ghost" style={{ justifyContent: 'flex-start', padding: 14 }} onClick={openFolder}>Folder upload</button>
        <button className="btn ghost" style={{ justifyContent: 'flex-start', padding: 14 }} onClick={() => modelInput.current?.click()}>3D model (.3dm .stl .glb)</button>
        <button className="btn ghost" style={{ justifyContent: 'flex-start', padding: 14 }} disabled={busy} onClick={() => assetInput.current?.click()}>Renders / photos</button>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files); }}
        onClick={() => modelInput.current?.click()}
        style={{ border: '2px dashed var(--border-strong)', borderRadius: 14, marginTop: 12, padding: 34, textAlign: 'center', background: drag ? 'var(--accent-soft)' : 'var(--surface-2)', cursor: 'pointer' }}
      >
        <div style={{ marginBottom: 8, color: 'var(--accent-strong)', opacity: .8 }}><Ic n="upload" size={26} sw={1.5} /></div>
        <b>{busy || uploads.length ? 'Uploading…' : 'Drag files anywhere'}</b>
        <div className="hint" style={{ marginTop: 4 }}>CAD files get auto-previews, weight estimates &amp; render queue on upload.<br />Uploads go direct to your S3 bucket.</div>
      </div>
      <input ref={modelInput} type="file" accept=".glb,.gltf,.3dm,.obj,.fbx,.stl" multiple hidden onChange={(e) => doUpload(e.target.files)} />
      <input ref={folderInput} type="file" multiple hidden onChange={(e) => doUpload(e.target.files)} />
      <input ref={assetInput} type="file" accept="image/*,application/pdf,.doc,.docx" multiple hidden onChange={(e) => void pickAssets(e.target.files)} />
    </Modal>
  );
}

/* Right-click / kebab menu — acts on the model in modalData.ctx (set by FileCard).
 * ponytail: mutations toast but can't refetch the open list from here (no drive
 * handle) — the change shows on next navigation. */
function CtxModal() {
  const { modals, close, open, go, modalData, setModalData } = useApp();
  const { toast } = useToast();
  const { user } = useAuth();
  const model = (modalData.ctx as VaultModel | null) ?? null;
  const [moveOpen, setMoveOpen] = useState(false);
  const [folders, setFolders] = useState<VaultFolder[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!modals.ctx) { setMoveOpen(false); setFolders(null); } }, [modals.ctx]);

  const done = (title: string) => { toast({ tone: "success", title }); close("ctx"); };
  const fail = (e: unknown, title: string) => toast({ tone: "error", title, description: e instanceof Error ? e.message : undefined });

  const rename = async () => {
    if (!model) return;
    const name = (await promptDialog({ title: "Rename file", label: "Name", defaultValue: model.name }))?.trim();
    if (!name || name === model.name) return;
    setBusy(true);
    try { await updateModel(model._id, { name }); done("File renamed"); }
    catch (e) { fail(e, "Couldn't rename"); } finally { setBusy(false); }
  };
  const star = async () => {
    if (!model) return;
    setBusy(true);
    try { const r = await starModel(model._id); done(r.starred ? "Added to starred" : "Removed from starred"); }
    catch (e) { fail(e, "Couldn't update"); } finally { setBusy(false); }
  };
  const trash = async () => {
    if (!model) return;
    if (!(await confirmDialog({ title: `Move “${model.name}” to trash?`, danger: true, confirmLabel: "Move" }))) return;
    setBusy(true);
    try { await deleteModel(model._id); done("Moved to trash"); }
    catch (e) { fail(e, "Couldn't move to trash"); } finally { setBusy(false); }
  };
  const openMove = () => {
    setMoveOpen(true);
    if (folders === null) listChildren(null).then((r) => setFolders(r.folders)).catch(() => setFolders([]));
  };
  const moveTo = async (folderId: string | null) => {
    if (!model) return;
    setBusy(true);
    try { await updateModel(model._id, { folderId }); done("File moved"); }
    catch (e) { fail(e, "Couldn't move"); } finally { setBusy(false); }
  };
  const shareIt = () => { if (model) setModalData("share", model); close("ctx"); open("share"); };

  return (
    <Modal id="ctx" maxWidth={280} plain>
      {moveOpen ? (
        <>
          <button className="nav-item" onClick={() => setMoveOpen(false)}>‹ Back</button>
          <div className="nav-label" style={{ padding: "6px 10px 2px" }}>Move to</div>
          <button className="nav-item" disabled={busy} onClick={() => void moveTo(null)}>My Drive (root)</button>
          {folders === null ? (
            <div className="hint" style={{ padding: "8px 12px" }}>Loading folders…</div>
          ) : folders.length === 0 ? (
            <div className="hint" style={{ padding: "8px 12px" }}>No folders yet.</div>
          ) : folders.map((f) => (
            <button key={f._id} className="nav-item" disabled={busy} onClick={() => void moveTo(f._id)}>{f.name}</button>
          ))}
        </>
      ) : (
        <>
          <button className="nav-item" onClick={shareIt}>Share…</button>
          <button className="nav-item" onClick={() => { close("ctx"); go("render"); }}>Render settings…</button>
          <button className="nav-item" onClick={() => { close("ctx"); go("collections"); }}>Add to collection…</button>
          <button className="nav-item" disabled={!model || busy} onClick={() => void rename()}>Rename</button>
          <button className="nav-item" disabled={!model || busy} onClick={openMove}>Move to…</button>
          <button className="nav-item" disabled={!model || busy} onClick={() => void star()}>{user && model?.starredBy?.includes(user.id) ? "Remove from starred" : "Add to starred"}</button>
          <button className="nav-item" style={{ color: 'var(--red)' }} disabled={!model || busy} onClick={() => void trash()}>Move to trash</button>
        </>
      )}
    </Modal>
  );
}

/* ───────────── collections pickers ───────────── */
/** Real model picker (drive-root models); selection tracked by model _id. */
function useModelPicker(enabled: boolean) {
  const [sel, setSel] = useState<string[]>([]);
  const [models, setModels] = useState<VaultModel[] | null>(null);
  useEffect(() => {
    if (!enabled) return;
    setModels(null); setSel([]);
    let alive = true;
    listModels(null, {}).then((m) => alive && setModels(m)).catch(() => alive && setModels([]));
    return () => { alive = false; };
  }, [enabled]);
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const el = models === null ? (
    <div className="hint" style={{ padding: "12px 0" }}>Loading your models…</div>
  ) : models.length === 0 ? (
    <div className="hint" style={{ padding: "12px 0" }}>No models in your drive yet — upload one first.</div>
  ) : (
    <div className="pick-grid">
      {models.map((m) => (
        <div key={m._id} className={"pick-it" + (sel.includes(m._id) ? " sel" : "")} onClick={() => toggle(m._id)} title={m.name}>
          {m.posterUrl
            ? <img src={m.posterUrl} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", background: "linear-gradient(150deg,#FBF7EE,#EFE7D5)" }} />}
          <span className="pk-n">{m.name}</span>
        </div>
      ))}
    </div>
  );
  return { sel, count: sel.length, el };
}

function NewColModal() {
  const { modals, close, go } = useApp();
  const { toast } = useToast();
  const picker = useModelPicker(!!modals.newCol);
  const [name, setName] = useState("");
  const [intro, setIntro] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!modals.newCol) { setName(""); setIntro(""); } }, [modals.newCol]);

  const create = async () => {
    if (!name.trim()) { toast({ tone: "error", title: "Name your collection first" }); return; }
    setBusy(true);
    try {
      const c = await createCollection({ name: name.trim(), intro: intro.trim() || undefined, fileIds: picker.sel });
      close("newCol");
      go(`/collections/${c._id}`);
    } catch (e) {
      toast({ tone: "error", title: "Couldn't create collection", description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal id="newCol" maxWidth={560} title="New collection">
      <div className="field" style={{ marginTop: 0 }}><label>Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Diwali '26 — statement gold" /></div>
      <div className="field"><label>Intro shown to clients</label>
        <input type="text" value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="One line that sets up the story…" /></div>
      <div className="field"><label>Pick pieces · from your drive, originals stay where they are</label>{picker.el}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
        <span className="hint" style={{ flex: 1 }}>Tip: right-click any file → “Add to collection”.</span>
        <button className="btn ghost" onClick={() => close('newCol')}>Cancel</button>
        <button className="btn primary" disabled={busy || !name.trim()} onClick={() => void create()}>{busy ? "Creating…" : `Create collection${picker.count ? ` (${picker.count})` : ''}`}</button>
      </div>
    </Modal>
  );
}

function AddItemsModal() {
  const { modals, close, modalData } = useApp();
  const { toast } = useToast();
  const collectionId = (modalData.addItems as { collectionId?: string } | undefined)?.collectionId;
  const picker = useModelPicker(!!modals.addItems && !!collectionId);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!collectionId || !picker.count) return;
    setBusy(true);
    try { await addItems(collectionId, picker.sel); close("addItems"); }
    catch (e) { toast({ tone: "error", title: "Couldn't add pieces", description: e instanceof Error ? e.message : undefined }); }
    finally { setBusy(false); }
  };

  return (
    <Modal id="addItems" maxWidth={560} title="Add pieces to collection">
      {!collectionId
        ? <div className="hint" style={{ padding: "8px 0" }}>Open a collection first, then use “Add pieces”.</div>
        : picker.el}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={() => close('addItems')}>Cancel</button>
        <button className="btn primary" disabled={!collectionId || !picker.count || busy} onClick={() => void add()}>{busy ? "Adding…" : `Add to collection${picker.count ? ` (${picker.count})` : ''}`}</button>
      </div>
    </Modal>
  );
}

/* ───────────── command palette ───────────── */
function CmdkModal() {
  const { modals, close, go, open, setClient } = useApp();
  const [q, setQ] = useState('');
  const [models, setModels] = useState<VaultModel[]>([]);
  const [cols, setCols] = useState<Collection[]>([]);

  useEffect(() => {
    if (!modals.cmdk) return;
    setQ('');
    let alive = true;
    listModels(null, {}).then((m) => alive && setModels(m)).catch(() => {});
    listCollections().then((c) => alive && setCols(c)).catch(() => {});
    return () => { alive = false; };
  }, [modals.cmdk]);

  if (!modals.cmdk) return null;
  const run = (fn: () => void) => { close('cmdk'); setQ(''); fn(); };
  const ql = q.toLowerCase();
  const has = (s: string) => s.toLowerCase().includes(ql);

  const NAV: [string, () => void][] = [
    ['My Drive', () => go('drive')], ['Collections', () => go('collections')],
    ['Canvas', () => go('playground')], ['Approvals', () => go('approvals')],
    ['Insights', () => go('insights')], ['Render settings', () => go('render')],
    ['Bulk Render', () => go('bulkrender')], ['Storage / S3', () => go('s3')],
    ['Team', () => go('team')], ['Plans & billing', () => go('billing')],
  ];
  const ACTIONS: [string, () => void][] = [
    ['Upload files…', () => open('upload')],
    ['New collection…', () => open('newCol')],
    ['Preview client view', () => setClient(true)],
    ['Bulk render…', () => open('bulk')],
    ['Open render queue', () => open('queue')],
  ];
  const navHits = NAV.filter(([l]) => has(l));
  const fileHits = (q ? models.filter((m) => has(m.name) || has(m.category ?? '')) : models).slice(0, 8);
  const colHits = (q ? cols.filter((c) => has(c.name)) : cols).slice(0, 6);
  const actionHits = ACTIONS.filter(([l]) => has(l));
  const hasAny = navHits.length || fileHits.length || colHits.length || actionHits.length;

  return (
    <div className="overlay open" onClick={e => { if (e.target === e.currentTarget) close('cmdk') }}>
      <div className="modal cmdk" style={{ alignSelf: 'flex-start', marginTop: '8vh' }}>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search everything — files, collections, actions…" />
        <div className="ck-list">
          {navHits.length > 0 && <div className="ck-sec">Jump to</div>}
          {navHits.map(([l, fn], i) => <button key={'n' + i} className="ck-item" onClick={() => run(fn)}>{l}</button>)}
          {fileHits.length > 0 && <div className="ck-sec">Files</div>}
          {fileHits.map((m) => <button key={m._id} className="ck-item" onClick={() => run(() => go(`/view/${m._id}`))}>{m.name}{m.category ? ` · ${m.category}` : ''}</button>)}
          {colHits.length > 0 && <div className="ck-sec">Collections</div>}
          {colHits.map((c) => <button key={c._id} className="ck-item" onClick={() => run(() => go(`/collections/${c._id}`))}>{c.name}</button>)}
          {actionHits.length > 0 && <div className="ck-sec">Actions</div>}
          {actionHits.map(([l, fn], i) => <button key={'a' + i} className="ck-item" onClick={() => run(fn)}>{l}</button>)}
          {!hasAny && <div className="ck-sec" style={{ padding: '16px 12px' }}>No matches for “{q}” — try a model name, collection or action.</div>}
        </div>
      </div>
    </div>
  );
}

/* ───────────── render queue / activity ───────────── */
function QueueModal() {
  const { modals } = useApp();
  const [batches, setBatches] = useState<RenderBatch[] | null>(null);
  useEffect(() => {
    if (!modals.queue) return;
    setBatches(null);
    let alive = true;
    listBatches().then((b) => alive && setBatches(b)).catch(() => alive && setBatches([]));
    return () => { alive = false; };
  }, [modals.queue]);

  const active = (batches ?? []).filter((b) => b.status === "queued" || b.status === "running");
  const running = active.filter((b) => b.status === "running").length;
  const queued = active.filter((b) => b.status === "queued").length;

  return (
    <Modal id="queue" maxWidth={560} title="Render queue" head={batches ? <span className="badge gold">{running} rendering · {queued} queued</span> : undefined}>
      {batches === null ? (
        <div className="hint" style={{ padding: '12px 0' }}>Loading render queue…</div>
      ) : active.length === 0 ? (
        <div className="anno">No renders in the queue. Select files and hit Bulk render to queue a batch — it keeps running even after you close the tab.</div>
      ) : (
        <>
          {active.map((b) => {
            const pct = b.totalJobs > 0 ? Math.round((b.doneJobs / b.totalJobs) * 100) : 0;
            const onprem = b.compute === "onprem";
            return (
              <div className="qrow" key={b._id}>
                <span className="mini-ico" style={{ background: onprem ? "#5B5F66" : "#A0793D" }}>{onprem ? "GPU" : "CLD"}</span>
                <div style={{ width: 190 }}>
                  <b style={{ fontSize: 12.5 }}>Batch · {b.totalJobs} job{b.totalJobs === 1 ? "" : "s"}</b>
                  <div className="hint">{b.resolution ?? "—"}{b.dpi ? ` · ${b.dpi} dpi` : ""} · {b.status}</div>
                </div>
                <div className="prog"><i style={{ width: pct + "%" }}></i></div>
                <span className="badge gold">{b.doneJobs}/{b.totalJobs}</span>
              </div>
            );
          })}
          <div className="anno">ⓘ Renders finish even after you close the tab — clients auto-see new renders on live links.</div>
        </>
      )}
    </Modal>
  );
}
function ActivityModal() {
  const { modals } = useApp();
  const [items, setItems] = useState<ActivityItem[] | null>(null);
  useEffect(() => {
    if (!modals.activity) return;
    setItems(null);
    let alive = true;
    getActivity().then((r) => alive && setItems(r)).catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [modals.activity]);

  return (
    <Modal id="activity" maxWidth={480} title="Activity">
      {items === null ? (
        <div className="hint" style={{ padding: '12px 0' }}>Loading activity…</div>
      ) : items.length === 0 ? (
        <div className="anno">No activity yet. Views, downloads, comments and approvals across your workspace appear here as clients open your share links.</div>
      ) : (
        items.map((it, i) => (
          <div key={i} className="feed-it">
            <span className="feed-ic" style={{ background: ACT_BG[it.type] }}><Ic n={ACT_ICON[it.type]} size={14} /></span>
            <div>{it.clientName ?? "Someone"} {ACT_VERB[it.type]} {it.fileName ?? "a file"}<div className="ft">{relTime(it.ts)}</div></div>
          </div>
        ))
      )}
    </Modal>
  );
}

/* ───────────── version compare slider ───────────── */
/** Real version comparison: two saved snapshots' config differences + restore.
 *  (Model versions store scene-config JSON, not paired render images, so this is a
 *   genuine settings diff rather than a before/after image slider.) */
function CompareModal() {
  const { close, modalData } = useApp();
  const { toast } = useToast();
  const model = modalData.compare as VaultModel | undefined;
  const [versions, setVersions] = useState<ModelVersion[] | null>(null);
  const [aId, setAId] = useState<string>("");
  const [bId, setBId] = useState<string>("");
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!model) { setVersions(null); return; }
    let alive = true;
    listVersions(model._id).then((v) => {
      if (!alive) return;
      setVersions(v);
      if (v.length >= 2) { setBId(v[0]._id); setAId(v[1]._id); }          // v[0] = newest
      else if (v.length === 1) { setAId(v[0]._id); setBId(v[0]._id); }
    }).catch(() => alive && setVersions([]));
    return () => { alive = false; };
  }, [model]);

  const a = versions?.find((v) => v._id === aId);
  const b = versions?.find((v) => v._id === bId);
  const fmt = (x: unknown) => (x === undefined ? "—" : typeof x === "object" ? JSON.stringify(x) : String(x));
  const diff = (() => {
    if (!a || !b) return [] as { k: string; a: unknown; b: unknown }[];
    const ca = (a.config ?? {}) as Record<string, unknown>;
    const cb = (b.config ?? {}) as Record<string, unknown>;
    return [...new Set([...Object.keys(ca), ...Object.keys(cb)])].sort()
      .filter((k) => JSON.stringify(ca[k]) !== JSON.stringify(cb[k]))
      .map((k) => ({ k, a: ca[k], b: cb[k] }));
  })();
  const label = (v: ModelVersion, i: number) => `v${v.version}${i === 0 ? " · current" : ""} · ${new Date(v.createdAt).toLocaleDateString()}`;

  const doRestore = async () => {
    if (!model || !a) return;
    setRestoring(true);
    try { await restoreVersion(model._id, a._id); toast({ tone: "success", title: `Restored version ${a.version}` }); close("compare"); }
    catch (e) { toast({ tone: "error", title: "Restore failed", description: e instanceof Error ? e.message : undefined }); }
    finally { setRestoring(false); }
  };

  return (
    <Modal id="compare" maxWidth={640} title={model ? `${model.name} — compare versions` : "Compare versions"}>
      {!model ? (
        <div className="hint">Open a model's Preview → Versions tab and hit “Compare versions”.</div>
      ) : versions === null ? (
        <div className="hint">Loading versions…</div>
      ) : versions.length < 2 ? (
        <div className="hint">Only one saved version so far — save the scene again in Canvas to create a second snapshot to compare.</div>
      ) : (
        <>
          <div className="frow" style={{ marginTop: 0 }}>
            <div className="field" style={{ marginTop: 0 }}><label>Version A</label>
              <select value={aId} onChange={(e) => setAId(e.target.value)}>{versions.map((v, i) => <option key={v._id} value={v._id}>{label(v, i)}</option>)}</select></div>
            <div className="field" style={{ marginTop: 0 }}><label>Version B</label>
              <select value={bId} onChange={(e) => setBId(e.target.value)}>{versions.map((v, i) => <option key={v._id} value={v._id}>{label(v, i)}</option>)}</select></div>
          </div>
          {(a?.note || b?.note) && <div className="hint" style={{ marginTop: 8 }}>{a?.note ? `A: "${a.note}"` : ""}{a?.note && b?.note ? " · " : ""}{b?.note ? `B: "${b.note}"` : ""}</div>}
          <div className="sec-head" style={{ margin: "16px 2px 8px" }}>Changed settings <span className="n">· {diff.length}</span></div>
          {diff.length === 0 ? (
            <div className="hint">These two versions have identical saved settings.</div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {diff.map((d) => <div key={d.k} className="kv"><b>{d.k}</b><span>{fmt(d.a)} → <b style={{ color: "var(--accent-strong)" }}>{fmt(d.b)}</b></span></div>)}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="btn ghost" style={{ flex: 1, justifyContent: "center" }} disabled={restoring} onClick={() => void doRestore()}>↩ Restore {a ? `v${a.version}` : "A"}</button>
            <button className="btn primary" style={{ flex: 1, justifyContent: "center" }} onClick={() => close("compare")}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ───────────── bulk render ───────────── */
const BULK_RES: [string, string][] = [["2560x1440", "2K"], ["3840x2160", "4K"], ["7680x4320", "8K"]];
const BULK_ANGLES: [string, string][] = [["perspective", "Perspective ¾"], ["macro", "Macro (stone)"], ["top", "Top"], ["front", "Front"]];
const BULK_METALS: [string, string][] = [["yellow-gold", "18k Yellow"], ["rose-gold", "18k Rose"], ["white-gold", "14k White"], ["platinum", "Platinum"]];

function BulkModal() {
  const { modals, close, open, go, selected, clearSel } = useApp();
  const { toast } = useToast();
  const [compute, setCompute] = useState<"cloud" | "onprem">("cloud");
  const [resolution, setResolution] = useState("3840x2160");
  const [angles, setAngles] = useState<string[]>(["perspective", "macro", "top", "front"]);
  const [metals, setMetals] = useState<string[]>(["yellow-gold"]);
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!modals.bulk) return;
    let alive = true;
    listAgents().then((a) => alive && setAgents(a)).catch(() => alive && setAgents([]));
    return () => { alive = false; };
  }, [modals.bulk]);

  const online = (agents ?? []).filter((a) => a.status === "online").length;
  const n = selected.length;
  const imgs = n * Math.max(1, angles.length) * Math.max(1, metals.length);
  const mins = Math.max(2, Math.round(imgs * 9.5 / 60));
  const toggle = (arr: string[], set: (v: string[]) => void, v: string) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const queue = async () => {
    if (!n) return;
    const target = compute === "onprem" && online === 0 ? "cloud" : compute;
    setBusy(true);
    try {
      await createBatch({ fileIds: selected, compute: target, resolution, dpi: 300, options: { angles, metals } });
      toast({ tone: "success", title: "Bulk render queued", description: `${n} file${n === 1 ? "" : "s"} → ${imgs} image${imgs === 1 ? "" : "s"}` });
      clearSel();
      close("bulk");
      open("queue");
    } catch (e) {
      toast({ tone: "error", title: "Couldn't queue render", description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal id="bulk" title="Bulk render" head={<span className="hint">runs in background</span>}>
      <div className="field" style={{ marginTop: 0 }}><label>What to render</label>
        <div className="pill-opts">
          <button className="on" disabled>Selected ({n})</button>
        </div>
        {n === 0 && <div className="hint" style={{ marginTop: 6 }}>Select files in your drive first, or use the Bulk Render studio to render a whole folder.</div>}
      </div>
      <div className="field" style={{ marginTop: 10 }}><label>Where to render</label>
        <div className="pill-opts">
          <button className={compute === 'cloud' ? 'on' : ''} onClick={() => setCompute('cloud')}>Vault Cloud</button>
          <button className={compute === 'onprem' ? 'on' : ''} disabled={online === 0} style={online === 0 ? { opacity: .5 } : undefined} onClick={() => setCompute('onprem')}>
            On-premises ({agents === null ? "…" : online > 0 ? `${online} online` : "no agents"})
          </button>
        </div>
      </div>
      <div className="field"><label>Resolution</label>
        <div className="pill-opts">
          {BULK_RES.map(([val, lbl]) => <button key={val} className={resolution === val ? 'on' : ''} onClick={() => setResolution(val)}>{lbl}</button>)}
        </div>
      </div>
      <div className="frow">
        <div className="field"><label>Camera angles</label>
          <div className="pill-opts">
            {BULK_ANGLES.map(([val, lbl]) => <button key={val} className={angles.includes(val) ? 'on' : ''} onClick={() => toggle(angles, setAngles, val)}>{lbl}</button>)}
          </div>
        </div>
        <div className="field"><label>Metal variants</label>
          <div className="pill-opts">
            {BULK_METALS.map(([val, lbl]) => <button key={val} className={metals.includes(val) ? 'on' : ''} onClick={() => toggle(metals, setMetals, val)}>{lbl}</button>)}
          </div>
        </div>
      </div>
      <div className="bulk-est" style={{ marginTop: 14 }}>
        ≈ <b>{imgs.toLocaleString()} images</b> ({n.toLocaleString()} file{n === 1 ? "" : "s"} × {Math.max(1, angles.length)} angle{angles.length === 1 ? "" : "s"} × {Math.max(1, metals.length)} metal{metals.length === 1 ? "" : "s"}) · <b>~{mins > 90 ? Math.round(mins / 60) + ' h' : mins + ' min'}</b> · finishes even if you close the tab.
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button className="btn ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => { close('bulk'); go('bulkrender') }}>Advanced → Bulk Render studio</button>
        <button className="btn gold" style={{ flex: 1.4, justifyContent: 'center' }} disabled={!n || busy || !angles.length || !metals.length} onClick={() => void queue()}><Ic n="bolt" size={14} /> {busy ? "Queuing…" : "Queue bulk render"}</button>
      </div>
    </Modal>
  );
}

export default function VaultModals() {
  return (<>
    <PreviewModal /><ShareModal /><UploadModal /><CtxModal />
    <NewColModal /><AddItemsModal />
    <CmdkModal /><QueueModal /><ActivityModal /><CompareModal /><BulkModal />
  </>);
}
